import * as Api from '@ViewModels';
import { startOfDay } from 'date-fns';
import { getDisplayName } from '../../../../models/NameUtils';
import { actionItemTintColor, eventTintColor } from '../../../styles/colors';
import { CalendarItem } from '../types';
import { getMeetingStatusDisplay, isMeetingCancelled } from './status';

/** App-standard event-registration tint — matches the Surveys event report. */
export const EVENT_COLOR = eventTintColor;

/** App-standard action-item tint — matches the action-item accent used elsewhere. */
export const ACTION_ITEM_COLOR = actionItemTintColor;

/** Representative meeting color for the legend — the default "Scheduled" status. */
export const MEETING_LEGEND_COLOR = getMeetingStatusDisplay(Api.MeetingStatus.Scheduled).color;

/** Project a scheduled meeting into a calendar item, or null if it has no start or id. */
export function meetingToCalendarItem(meeting: Api.IScheduledMeeting): CalendarItem | null {
	// An id is required — without one, multiple items would collide on the same render key.
	if (!meeting.startDate || !meeting.id) {
		return null;
	}
	const status = getMeetingStatusDisplay(meeting.status);
	// A past-but-not-dispositioned meeting ("Scheduled (Past)") keeps the scheduled color and just
	// renders faded — one color per type reads cleaner on the calendar than a separate orange.
	const isPast = meeting.status === Api.MeetingStatus.WaitingForDisposition;
	return {
		kind: 'meeting',
		id: meeting.id,
		title: meeting.title || 'Untitled meeting',
		start: new Date(meeting.startDate),
		end: meeting.endDate ? new Date(meeting.endDate) : null,
		color: isPast ? MEETING_LEGEND_COLOR : status.color,
		subLabel: status.label,
		muted: isMeetingCancelled(meeting),
		faded: isPast,
		meeting,
	};
}

/** Project a hosted event (registration survey) into a calendar item, or null if it has no start. */
export function eventToCalendarItem(event: Api.IEventRegistrationSurvey): CalendarItem | null {
	const start = event.eventInformation?.startTime;
	if (!start || !event.id) {
		return null;
	}
	const end = event.eventInformation?.endTime;
	return {
		kind: 'event',
		id: event.id,
		title: event.name || 'Untitled event',
		start: new Date(start),
		end: end ? new Date(end) : null,
		color: EVENT_COLOR,
		subLabel: 'Event',
		muted: false,
		event,
	};
}

/**
 * Project an action item into an all-day calendar item. Action items carry only a due date, so
 * they render in the all-day band rather than the hourly grid.
 *
 * Undated items normally return null (they have no place on a calendar). When `showUndatedOnToday`
 * is set they are instead placed on today as a convenience and flagged `undated` so the views sort
 * them after real dated work and collapse the overflow. Callers pass that only when today is
 * actually visible.
 *
 * Overdue (incomplete, past-due) items keep the action-item color (so the calendar legend stays
 * accurate — color means type) and are instead flagged via the `overdue` cue. When
 * `rollOverdueToToday` is set they are placed on today instead of their (past) due date, so they
 * surface in the current view rather than being missed off-window. Callers pass that only when
 * today is actually visible.
 */
export function actionItemToCalendarItem(
	actionItem: Api.IActionItem,
	options?: { now?: Date; rollOverdueToToday?: boolean; showUndatedOnToday?: boolean }
): CalendarItem | null {
	const now = options?.now ?? new Date();
	// An id is required — without one, multiple items would collide on the same render key.
	if (!actionItem.id) {
		return null;
	}
	if (!actionItem.dueDate) {
		if (!options?.showUndatedOnToday) {
			return null;
		}
		const undatedAssignee = actionItem.assignee ? getDisplayName(actionItem.assignee) : '';
		return {
			kind: 'actionItem',
			id: actionItem.id,
			title: actionItem.preview || actionItem.plainTextContent || 'Action item',
			start: startOfDay(now),
			end: null,
			allDay: true,
			color: ACTION_ITEM_COLOR,
			subLabel: undatedAssignee ? `Assigned to ${undatedAssignee}` : 'No due date',
			muted: !!actionItem.isCompleted,
			undated: true,
			actionItem,
		};
	}
	const due = new Date(actionItem.dueDate);
	const isOverdue = !actionItem.isCompleted && due.getTime() < now.getTime();
	const start = isOverdue && options?.rollOverdueToToday ? startOfDay(now) : due;
	const assigneeName = actionItem.assignee ? getDisplayName(actionItem.assignee) : '';
	return {
		kind: 'actionItem',
		id: actionItem.id,
		title: actionItem.preview || actionItem.plainTextContent || 'Action item',
		start,
		end: null,
		allDay: true,
		color: ACTION_ITEM_COLOR,
		subLabel: isOverdue ? 'Overdue' : assigneeName ? `Assigned to ${assigneeName}` : 'Action item',
		muted: !!actionItem.isCompleted,
		overdue: isOverdue,
		actionItem,
	};
}
